/**
 * Upload one file as an artifact: initiate → PUT to the signed URL → finalize.
 *
 * One copy for the chat composer (HomePage) and the project page, so the two
 * cannot drift (the storage contract is Docs/architecture/06). Raw `fetch` only
 * for the signed URL, which is the storage backend's, not our API.
 */
import type { Client } from "@hey-api/client-fetch";
import { finalizeUpload, initiateUpload } from "@/api";

export async function uploadArtifact(
  client: Client,
  file: File,
): Promise<{ artifactId: string; key: string }> {
  const { data: init } = await initiateUpload({
    client,
    body: {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      // Keyed under artifacts/ with the artifact_id this flow needs.
      // "general" is not in the contract enum and 422s.
      purpose: "artifact",
    },
    throwOnError: true,
  });
  if (!init) {
    throw new Error("Could not start upload — check storage configuration");
  }

  const putRes = await fetch(init.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  });
  if (!putRes.ok) {
    const msg = await putRes.text().catch(() => "");
    throw new Error(
      msg.trim()
        ? `Upload failed (${putRes.status}): ${msg.slice(0, 120)}`
        : `Upload failed (${putRes.status})`,
    );
  }

  await finalizeUpload({
    client,
    path: { artifact_id: init.artifact_id },
    throwOnError: true,
  });

  return { artifactId: init.artifact_id, key: init.key };
}
